import { useCallback, useEffect, useMemo, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';
import delay from '@zenbusiness/application-commons-utils/delay';
import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import Box from '@zenbusiness/zui/Box';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import AstraAuthorizationModal from '#client/components/AstraAuthorizationModal';
import FormStepModal from '#client/components/FormStepModal';
import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import RenderIf from '#client/components/RenderIf';
import ScanToDownloadAppModal from '#client/components/ScanToDownloadAppModal';
import ViewAllTransactionLimitButton from '#client/components/ViewAllTransactionLimitButton';
import { ASTRA_STATUS } from '#client/constants/astra';
import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import { MODAL_NAMES } from '#client/constants/modals';
import { WHICH_ACCOUNT_STEP } from '#client/constants/transfer';
import useAddBankOrDeposit from '#client/hooks/useAddBankOrDeposit';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckDepositModal from '#client/hooks/useCheckDepositModal';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useInstantBankingCard from '#client/hooks/useInstantBankingCard';
import useInstantDepositLimit from '#client/hooks/useInstantDepositLimit';
import useMobileCheckDeposit from '#client/hooks/useMobileCheckDeposit';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useOpenAstraModal from '#client/hooks/useOpenAstraModal';
import useToastContext from '#client/hooks/useToastContext';
import { GET_LIST_COUNTERPARTY } from '#client/queries';
import { checkIsMobile } from '#client/utils/devices';

import InstantDepositModal from '../InstantDepositModal';
import MoveMoneyOption from './components/MoveMoneyOption';
import { OptionSkeleton } from './components/OptionSkeleton';

import { Wrapper, DialogContentStyled } from './index.styles';
import {
  SETTING_UP_ACCOUNT,
  TRY_ADD_CARD_AGAIN_STEP
} from '../InstantDepositModal/constants/instants';

export default function MoveMoneyChoiceViewModal({
  handleClose,
  actionsKit = null,
  counterParty = null,
  isHideCheckDepositOption = false,
  entryPageForSendMoney
}) {
  const { getAndRedirectAstraUrl } = useOpenAstraModal();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const theme = useTheme();
  const {
    cardList,
    hasAcceptedTerms,
    loading: loadingAstraProfile,
    isNeedCreateDestinationCard,
    refreshAstraProfile,
    isHideInstantDepositOption,
    accountStatus,
    shouldAttemptCreateAstraProfile
  } = useAstraProfileContext();
  const {
    isEnableMobileCheckDeposit,
    loading: loadingCustomerToken,
    launchCheckDeposit
  } = useMobileCheckDeposit();

  const openCheckDepositModal = useCheckDepositModal();

  const {
    error: instantDepositLimitError,
    loading: instantDepositLimitLoading,
    transfers: instantDepositTransfers
  } = useInstantDepositLimit();
  const { dailyTransferCountLimit, isDailyTransferLimitExceeded } = instantDepositTransfers;

  // Render instant deposit description based on transfer count
  const instantDepositOptionTexts = useMemo(
    () => ({
      description: isDailyTransferLimitExceeded
        ? `You reached the ${dailyTransferCountLimit} daily transaction limit.`
        : 'Visa® or Mastercard® debit card only.'
    }),
    [isDailyTransferLimitExceeded, dailyTransferCountLimit]
  );

  const [firstAstraProfileLoading, setFirstAstraProfileLoading] = useState(true);

  /**
   * Use this effect for checking loading skeleton only 1 time
   */
  useEffect(() => {
    if (!loadingAstraProfile) {
      setFirstAstraProfileLoading(false);
    }
  }, [loadingAstraProfile]);

  const { createBankingCardForInstantFunding, loading: createCardLoading } =
    useInstantBankingCard();

  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const { openModal, closeModal } = useModalContext();
  const { createCommonErrorToast } = useToastContext();

  // For instant deposit option
  const [isShowInstantDepositFlagEnabled] = useFeatureFlag(RELEASE_SHOW_INSTANT_DEPOSIT);

  const [getListCounterPartyCombine, { loading: loadingCounterPartyCombine }] = useManualQuery(
    GET_LIST_COUNTERPARTY,
    {
      noThrow: true
    }
  );

  const { openAddBankOrDeposit, loading: loadingCounterPartyPlaid } = useAddBankOrDeposit({
    actionsKit,
    counterParty
  });

  const isLoadingCounterparty = loadingCounterPartyPlaid || loadingCounterPartyCombine;

  useModalDisplayTracker({
    elementName: isHideInstantDepositOption ? 'move_money_modal' : 'move_money_modal_with_Astra',
    appPageName: 'active_banking_dashboard'
  });
  /**
   * Open send money modal handler
   * When counterparty is empty then open the add account modal for payee
   * When counterparty is not empty then open the send money modal (show list payee)
   */
  const [hasAccountCombineCache, setHasAccountCombineCache] = useLocalStorage(
    `has_counterparty_combine.${businessEntityUuid}`,
    false
  );
  const handleSendMoney = useCallback(async () => {
    const hasAccountCombine = hasAccountCombineCache === true;
    if (!hasAccountCombine) {
      const { data, error } = await getListCounterPartyCombine({
        variables: {
          filter: {
            businessEntityUuid
          },
          page: {
            pageSize: 1,
            page: 1
          }
        },
        skipCache: true
      });

      if (error || isUndefinedOrNull(data.bankingCounterparties)) {
        createCommonErrorToast({ error });
        return;
      }

      const accounts = data?.bankingCounterparties?.bankingCounterparties;
      const numberOfAccount = accounts?.length;
      if (numberOfAccount === 0) {
        openModal({
          name: MODAL_NAMES.LINK_BANK_ACCOUNT_MODAL,
          component: AddCounterPartyModal,
          mode: AddCounterPartyModal.MODAL_TYPES.ADD_BANK_AND_PAYEE,
          actionsKit
        });
        return;
      }

      if (numberOfAccount > 0) {
        setHasAccountCombineCache(true);
      }
    }

    openModal({
      name: MODAL_NAMES.SEND_MONEY_MODAL,
      counterParty,
      actionsKit,
      entryPageForSendMoney
    });
  }, [
    businessEntityUuid,
    hasAccountCombineCache,
    openModal,
    counterParty,
    actionsKit,
    entryPageForSendMoney,
    getListCounterPartyCombine,
    createCommonErrorToast,
    setHasAccountCombineCache
  ]);

  // Render options text based on the LD flag
  const moveMoneyOptionsWithLDFlag = useMemo(
    () => ({
      deposit: {
        title: isShowInstantDepositFlagEnabled ? 'ACH deposit' : 'Deposit money',
        description: isShowInstantDepositFlagEnabled
          ? 'Up to $10,000 per transaction. Takes 4 to 5 days'
          : 'Via bank transfer (ACH)'
      },
      checkDeposit: {
        title: 'Mobile check deposit'
      },
      sendMoney: {
        description: isShowInstantDepositFlagEnabled
          ? 'Via ACH or wire transfer to another account'
          : 'Via ACH or wire transfer'
      }
    }),
    [isShowInstantDepositFlagEnabled]
  );

  const trackClickCommon = {
    elementName: 'move_money_option',
    appPageName: isHideInstantDepositOption ? 'move_money_modal' : 'move_money_modal_with_Astra'
  };

  const trackDepositClick = useButtonPressedCallback({
    ...trackClickCommon,
    elementText: moveMoneyOptionsWithLDFlag.deposit.title
  });

  const trackInstantDepositClick = useButtonPressedCallback({
    ...trackClickCommon,
    elementText: 'Instant deposit'
  });

  const trackCheckDepositClick = useButtonPressedCallback({
    ...trackClickCommon,
    elementText: moveMoneyOptionsWithLDFlag.checkDeposit.title
  });

  const trackSendMoneyClick = useButtonPressedCallback({
    ...trackClickCommon,
    elementText: 'Send Money'
  });

  const handleInstantDeposit = useCallback(async () => {
    if (!hasAcceptedTerms) {
      openModal({
        component: AstraAuthorizationModal,
        actionsKit
      });
      return;
    }

    if (isNeedCreateDestinationCard) {
      const { error: createCardError } = await createBankingCardForInstantFunding();
      if (createCardError) {
        openModal({
          component: InstantDepositModal,
          initStep: TRY_ADD_CARD_AGAIN_STEP
        });

        return;
      }

      refreshAstraProfile();
    }

    if (hasAcceptedTerms === true && cardList && cardList.length === 0) {
      getAndRedirectAstraUrl();
      return;
    }

    openModal({
      component: InstantDepositModal,
      initStep: WHICH_ACCOUNT_STEP,
      actionsKit
    });
  }, [
    hasAcceptedTerms,
    isNeedCreateDestinationCard,
    cardList,
    openModal,
    createBankingCardForInstantFunding,
    refreshAstraProfile,
    getAndRedirectAstraUrl,
    actionsKit
  ]);

  /**
   * Triggers the flow for an instant deposit by refreshing the Astra profile,
   * handling the deposit, and then closing the Instant Deposit modal after a delay.
   */
  const triggerInstantDepositFlow = useCallback(async () => {
    // Refresh the Astra profile to ensure it's up to date
    await refreshAstraProfile();
    handleInstantDeposit();
    delay(closeModal(MODAL_NAMES.INSTANT_DEPOSIT_MODAL), 500);
  }, [closeModal, handleInstantDeposit, refreshAstraProfile]);

  const registerInstantFundingAccount = useCallback(async () => {
    openModal({
      component: InstantDepositModal,
      name: MODAL_NAMES.INSTANT_DEPOSIT_MODAL,
      initStep: SETTING_UP_ACCOUNT,
      actionsKit: {
        triggerInstantDepositFlow
      }
    });
  }, [triggerInstantDepositFlow, openModal]);

  const moveMoneyOptions = useMemo(
    () => [
      {
        optionsTitle: 'Send money from ZenBusiness',
        options: [
          {
            icon: (
              <SharedAsset
                alt=""
                height={24}
                src="/s2/icon/money-bag-fill.svg"
                width={24}
              />
            ),
            title: 'Send money',
            description: moveMoneyOptionsWithLDFlag.sendMoney.description,
            onClick: () => {
              trackSendMoneyClick();
              handleSendMoney();
            },
            isHide: false,
            isDisable: Boolean(isLoadingCounterparty || createCardLoading || loadingCustomerToken),
            isLoading: loadingCounterPartyCombine
          }
        ]
      },
      {
        optionsTitle: 'Add money to your account by',
        options: [
          {
            icon: (
              <SharedAsset
                alt=""
                height={24}
                src="/s2/icon/pig-fill.svg"
                width={24}
              />
            ),
            title: moveMoneyOptionsWithLDFlag.deposit.title,
            description: moveMoneyOptionsWithLDFlag.deposit.description,
            onClick: () => {
              trackDepositClick();
              openAddBankOrDeposit();
            },
            isHide: false,
            isDisable: Boolean(isLoadingCounterparty || createCardLoading || loadingCustomerToken),
            isLoading: loadingCounterPartyPlaid
          },
          {
            icon: (
              <SharedAsset
                alt=""
                height={24}
                src="/s2/icon/credit-card-material-outline.svg"
                width={24}
              />
            ),
            label: 'New',
            title: 'Instant deposit',
            description: instantDepositOptionTexts.description,
            onClick: () => {
              trackInstantDepositClick();

              if (shouldAttemptCreateAstraProfile) {
                registerInstantFundingAccount();
              } else {
                handleInstantDeposit();
              }
            },
            isHide: isHideInstantDepositOption,
            isDisable: Boolean(
              isLoadingCounterparty ||
              accountStatus === ASTRA_STATUS.SUSPENDED ||
              loadingAstraProfile ||
              createCardLoading ||
              loadingCustomerToken ||
              instantDepositLimitLoading ||
              isDailyTransferLimitExceeded ||
              instantDepositLimitError
            ),
            isLoading: createCardLoading
          },
          {
            icon: (
              <SharedAsset
                alt=""
                height={24}
                src="/s2/icon/camera-fill.svg"
                width={24}
              />
            ),
            title: moveMoneyOptionsWithLDFlag.checkDeposit.title,
            description: 'Available in our app',
            onClick: () => {
              trackCheckDepositClick();

              if (isEnableMobileCheckDeposit) {
                launchCheckDeposit();
              } else if (checkIsMobile()) {
                openCheckDepositModal({ actionsKit });
              } else {
                openModal({
                  component: ScanToDownloadAppModal,
                  name: MODAL_NAMES.SCAN_TO_DOWNLOAD_APP_MODAL
                });
              }
            },
            isDisable: Boolean(isLoadingCounterparty || createCardLoading || loadingCustomerToken),
            isHide: isHideCheckDepositOption,
            isLoading: loadingCustomerToken
          }
        ]
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      moveMoneyOptionsWithLDFlag.deposit.title,
      moveMoneyOptionsWithLDFlag.deposit.description,
      moveMoneyOptionsWithLDFlag.checkDeposit.title,
      moveMoneyOptionsWithLDFlag.sendMoney.description,
      isLoadingCounterparty,
      isHideInstantDepositOption,
      createCardLoading,
      loadingCustomerToken,
      loadingCounterPartyPlaid,
      accountStatus,
      registerInstantFundingAccount,
      loadingAstraProfile,
      isHideCheckDepositOption,
      loadingCounterPartyCombine,
      shouldAttemptCreateAstraProfile,
      openAddBankOrDeposit,
      handleInstantDeposit,
      isEnableMobileCheckDeposit,
      launchCheckDeposit,
      openModal,
      actionsKit,
      handleSendMoney,
      instantDepositLimitLoading,
      isDailyTransferLimitExceeded,
      instantDepositLimitError,
      instantDepositOptionTexts.description
    ]
  );

  const marginSpacing = isDesktop ? 10 : 3;

  return (
    <FormStepModal
      disableBack
      handleClose={handleClose}
      isHideBackButton
      title="Move Money"
      titleBackground={theme.palette.background.grey.main}
    >
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper
              my={marginSpacing}
              role="option"
            >
              <RenderIf
                fallback={<OptionSkeleton />}
                when={!firstAstraProfileLoading}
              >
                <Stack gap="36px">
                  {moveMoneyOptions.map(({ optionsTitle, options }) => (
                    <Stack
                      key={optionsTitle}
                      spacing="20px"
                    >
                      <Text
                        color="primary"
                        size="xl"
                        weight="medium"
                      >
                        {optionsTitle}
                      </Text>
                      <Stack spacing={{ xs: '16px', md: '8px' }}>
                        {options.map(
                          ({
                            icon,
                            title,
                            description,
                            onClick,
                            label,
                            isHide,
                            isDisable,
                            isLoading
                          }) => (
                            <RenderIf
                              key={title}
                              when={!isHide}
                            >
                              <MoveMoneyOption
                                description={description}
                                icon={icon}
                                isDisable={isDisable}
                                isLoading={isLoading}
                                label={label}
                                onClick={onClick}
                                title={title}
                              />
                            </RenderIf>
                          )
                        )}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </RenderIf>
              <Box
                alignSelf="center"
                mt={{ xs: '36px', md: '40px' }}
              >
                <ViewAllTransactionLimitButton isAll />
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
    </FormStepModal>
  );
}

MoveMoneyChoiceViewModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  }),
  isHideCheckDepositOption: PropTypes.bool,
  entryPageForSendMoney: PropTypes.string,
  counterParty: PropTypes.shape({
    id: PropTypes.string.isRequired,
    bank: PropTypes.string.isRequired,
    accountNumber: PropTypes.string.isRequired,
    accountType: PropTypes.string.isRequired
  })
};
